import React, { useMemo } from "react";
import { useTheme } from "@emotion/react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Card, CardContent, Typography, Box } from "@mui/material";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Bar,
  BarChart,
  AreaChart,
  Area,
  ReferenceLine,
  Cell,
} from "recharts";
import moment from "moment";
// Ajout des imports pour les icônes
import { Bolt, WaterDrop, Whatshot, TrendingUp, TrendingDown } from "@mui/icons-material";
import { BatteryIcon } from "../icons/am103";

function ThresholdPillLabel({ viewBox, value, bg, fg }) {
  if (!viewBox) return null;
  const text = String(value);
  const fontSize = 11;
  const charWidth = 7;
  const padX = 8;
  const padY = 3;
  const width = text.length * charWidth + padX * 2;
  const height = fontSize + padY * 2;
  const cx = viewBox.x - width / 2 - 2;
  const cy = viewBox.y;
  return (
    <g>
      <rect
        x={cx - width / 2}
        y={cy - height / 2}
        width={width}
        height={height}
        rx={height / 2}
        ry={height / 2}
        fill={bg}
      />
      <text
        x={cx}
        y={cy + fontSize / 3}
        textAnchor="middle"
        fontSize={fontSize}
        fontWeight={700}
        fill={fg}
      >
        {text}
      </text>
    </g>
  );
}

export default function DeviceGraph(props) {
  const {
    value,
    data,
    aspect,
    groupedBy,
    unit,
    chartType = "bar",
    typeName,
    temperatureLabel,
    title,
    subtitle,
    onBarClick,
    thresholds, // { yellow: number|null, red: number|null } — seuils journaliers
    colorOverride, // couleur unique forcée (barres + bordure + tooltip), ex. bornes Plugy
    bare, // rendu épuré : pas de carte (fond/bordure/ombre) ni d'en-tête (titre/icône)
  } = props;

  // Vérifier si c'est un device vanne thermo / TRV
  const isVanneThermo = typeName?.toLowerCase()?.includes("vanne") ||
                        typeName?.toLowerCase()?.includes("thermo") ||
                        typeName?.toLowerCase()?.includes("trv");
  // Définir valueName selon le type
  let valueName;
  if (typeName?.toLowerCase() === "humidity") {
    valueName = "humidity";
  } else if (typeName?.toLowerCase() === "co2") {
    valueName = "co2";
  } else if (typeName?.toLowerCase() === "temperature") {
    valueName = "temperature";
  } else {
    valueName = value;
  }
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isLightMode = document.documentElement.getAttribute('data-theme') === 'light';
  
  
  const filteredData = data?.filter(item => {
    const typeLower = typeName?.toLowerCase();
    if (typeLower === "temperature" || typeLower === "humidity" || typeLower === "co2") {
      // Pour température, humidité et CO2, éliminer null, undefined et 0 (qui indique absence de données)
      return item[valueName] != null && item[valueName] !== 0;
    } else {
      // Pour eau/électricité, éliminer seulement null et undefined
      return item[valueName] != null;
    }
  }) || [];

  // Calculer la moyenne pour la ligne de référence
  const averageValue = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return 0;
    const sum = filteredData.reduce((acc, item) => acc + (item[valueName] || 0), 0);
    return Math.round((sum / filteredData.length) * 100) / 100;
  }, [filteredData, valueName]);

  // Calculer le max pour les animations
  const maxValue = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return 0;
    return Math.max(...filteredData.map(item => item[valueName] || 0));
  }, [filteredData, valueName]);

  // State pour l'index survolé
  const [hoveredIndex, setHoveredIndex] = React.useState(null);

  // Fonction pour obtenir l'icône selon le type
  const getTypeIcon = () => {
    const typeLower = typeName?.toLowerCase() || "";

    // Icônes existantes
    if (typeLower.includes("water")) {
      return <WaterDrop sx={{ fontSize: 24, color: "#4fc3f7", opacity: 0.8 }} />;
    }
    if (typeLower.includes("electric")) {
      return <Bolt sx={{ fontSize: 24, color: "#ffd54f", opacity: 0.8 }} />;
    }
    // Température (flocon pour chambres froides, thermomètre pour AM103)
    if (typeLower.includes("temperature")) {
      if (typeLower.includes("froide") || typeLower.includes("cold")) {
        return <span style={{ fontSize: 20, opacity: 0.9 }}>❄️</span>;
      }
      return <span style={{ fontSize: 20, opacity: 0.9 }}>🌡️</span>;
    }

    // AM103 - Humidité
    if (typeLower.includes("humidity")) {
      return <span style={{ fontSize: 20, opacity: 0.9 }}>💧</span>;
    }
    // AM103 - CO2
    if (typeLower.includes("co2")) {
      return <span style={{ fontSize: 20, opacity: 0.9 }}>🌬️</span>;
    }
    if (typeLower.includes("battery")) {
      return <BatteryIcon sx={{ fontSize: 24, color: "#ff9800", opacity: 0.8 }} level={85} />;
    }
    // Icône pour vanne thermo / TRV
    if (typeLower.includes("vanne") || typeLower.includes("thermo") || typeLower.includes("trv")) {
      return <Whatshot sx={{ fontSize: 24, color: "#e67e22", opacity: 0.8 }} />;
    }

    return null;
  };

  // Format date for axis
  const formatTime = (time) => {
    if (groupedBy === "daily") {
      return moment(time).format("DD/MM");
    } else if (groupedBy === "hourly") {
      return moment(time).format("HH:mm");
    } else {
      return moment(time).format("DD/MM HH:mm");
    }
  };

  // Remove duplicate timestamps for daily view
  const processedData = React.useMemo(() => {
    if (groupedBy === "daily") {
      const seen = new Set();
      return data.filter(item => {
        const dateKey = moment(item.time_created).format("DD/MM");
        if (seen.has(dateKey)) {
          return false;
        }
        seen.add(dateKey);
        return true;
      });
    }
    return data;
  }, [data, groupedBy]);

  // Formatter for tooltip
  const valueFormatter = (val, name) => {
    let label = valueName;
    if (name === "total_kwh") {
      label = "kWh";
    } else if (name === "total_liters") {
      label = "litres";
    }
    val = Math.round(val * 100) / 100;
    return [val + " " + unit, label];
  };

  // Fonction pour déterminer la couleur selon la valeur
  const getBarColor = (entry) => {
    const val = entry[value];
    if (value === "total_kwh") {
      if (val > 20) return "#f44336"; // Rouge
      if (val > 15) return "#ff9800"; // Orange
      return "#6bc769"; // Vert
    } else if (value === "total_liters") {
      if (val > 500) return "#f44336"; // Rouge
      if (val > 300) return "#ff9800"; // Orange
      return "#6bc769"; // Vert
    }
    return "#6bc769"; // Défaut
  };

  // Custom label formatter
  const renderCustomLabel = (value) => {
    return Math.round(value);
  };

  // Couleur selon le type
  const getTypeColor = () => {
    if (colorOverride) return colorOverride;
    const typeLower = typeName?.toLowerCase() || "";
    if (typeLower.includes("water")) return "#4fc3f7";
    if (typeLower.includes("electric")) return "#ffd54f";
    if (typeLower.includes("temperature")) return "#99DCFF";
    if (typeLower.includes("humidity")) return "#00bcd4";
    if (typeLower.includes("co2")) return "#4caf50";
    if (typeLower.includes("vanne") || typeLower.includes("thermo") || typeLower.includes("trv")) return "#F97316";
    return "#6bc769";
  };

  // Custom Tooltip Component - Version moderne
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const time = moment(label).format(groupedBy === "daily" ? "DD/MM" : "HH:mm");
      const currentValue = Math.round(payload[0].value * 100) / 100;
      const diffFromAvg = averageValue > 0 ? Math.round(((currentValue - averageValue) / averageValue) * 100) : 0;
      const typeColor = getTypeColor();

      return (
        <Box sx={{
          backgroundColor: isLightMode ? 'rgba(255, 255, 255, 0.98)' : 'rgba(15, 20, 25, 0.95)',
          backdropFilter: 'blur(10px)',
          p: 2,
          borderRadius: 3,
          border: `1px solid ${typeColor}40`,
          boxShadow: isLightMode
            ? `0 4px 16px rgba(0,0,0,0.15), 0 0 10px ${typeColor}15`
            : `0 8px 32px rgba(0,0,0,0.4), 0 0 20px ${typeColor}20`,
          minWidth: 120,
        }}>
          <Typography sx={{ color: isLightMode ? '#5a6b5a' : 'rgba(255,255,255,0.6)', fontSize: '11px', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {time}
          </Typography>
          <Typography sx={{ color: typeColor, fontSize: '28px', fontWeight: 'bold', lineHeight: 1.2 }}>
            {currentValue}
            <Typography component="span" sx={{ fontSize: '14px', ml: 0.5, color: isLightMode ? '#3d4a3d' : 'rgba(255,255,255,0.7)' }}>
              {unit}
            </Typography>
          </Typography>
          {averageValue > 0 && chartType === "bar" && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              {diffFromAvg >= 0 ? (
                <TrendingUp sx={{ fontSize: 14, color: diffFromAvg > 20 ? '#ef4444' : '#22c55e' }} />
              ) : (
                <TrendingDown sx={{ fontSize: 14, color: '#22c55e' }} />
              )}
              <Typography sx={{
                fontSize: '11px',
                color: diffFromAvg > 20 ? '#ef4444' : diffFromAvg < -20 ? '#22c55e' : (isLightMode ? '#5a6b5a' : 'rgba(255,255,255,0.5)'),
                fontWeight: 500
              }}>
                {diffFromAvg >= 0 ? '+' : ''}{diffFromAvg}% vs moy.
              </Typography>
            </Box>
          )}
        </Box>
      );
    }
    return null;
  };
  // Bordure colorée selon le type.
  // En light mode on prend les tokens --kpi-rail-* (mêmes que les KPI cards).
  const getBorderColor = () => {
    if (colorOverride) return colorOverride;
    const typeLower = typeName?.toLowerCase() || "";
    if (isLightMode) {
      if (typeLower.includes("water")) return "var(--kpi-rail-water)";
      if (typeLower.includes("electric")) return "var(--kpi-rail-elec)";
      if (typeLower.includes("temperature")) return "var(--kpi-rail-temp)";
      if (typeLower.includes("humidity")) return "var(--accent-sky)";
      if (typeLower.includes("co2")) return "var(--success, #5a7a4a)";
      if (typeLower.includes("vanne") || typeLower.includes("thermo") || typeLower.includes("trv")) return "var(--kpi-rail-trv)";
      return "var(--color-primary)";
    }
    if (typeLower.includes("water")) return "#4fc3f7";
    if (typeLower.includes("electric")) return "#ffd54f";
    if (typeLower.includes("temperature")) return "#99DCFF";
    if (typeLower.includes("humidity")) return "#00bcd4";
    if (typeLower.includes("co2")) return "#4caf50";
    if (typeLower.includes("vanne") || typeLower.includes("thermo") || typeLower.includes("trv")) return "#F97316";
    return "#6bc769";
  };

  // Points de dépassement de seuil pour le graphe température (area) :
  // un point rouge si la valeur dépasse le seuil rouge, jaune si elle dépasse
  // le seuil jaune, rien sinon. Même sens que le backend (au-dessus = alerte).
  const hasTempThresholds = thresholds && (thresholds.yellow > 0 || thresholds.red > 0 || thresholds.yellow < 0 || thresholds.red < 0);
  const renderThresholdDot = (dotProps) => {
    const { cx, cy, value, index } = dotProps;
    if (cx == null || cy == null || value == null) return <React.Fragment key={`d-${index}`} />;
    let color = null;
    if (thresholds?.red != null && value > thresholds.red) color = "#fe3e26";
    else if (thresholds?.yellow != null && value > thresholds.yellow) color = "#f5a623";
    if (!color) return <React.Fragment key={`d-${index}`} />;
    return (
      <circle key={`d-${index}`} cx={cx} cy={cy} r={3.5} fill={color} stroke="#fff" strokeWidth={1} />
    );
  };

  return (
    <Card sx={{
      position: "relative",
      backgroundColor: isLightMode ? "var(--bg-card)" : "rgba(25, 32, 28, 0.95)",
      backdropFilter: isLightMode ? "none" : "blur(10px)",
      borderRadius: isLightMode ? "var(--radius-tile, 14px) !important" : "12px !important",
      border: isLightMode ? "1px solid var(--border-light)" : "1px solid rgba(255,255,255,0.06)",
      borderTop: `3px solid ${getBorderColor()}`,
      boxShadow: isLightMode ? "var(--shadow-sm)" : "0 4px 24px rgba(0,0,0,0.4)",
      overflow: "hidden",
      transition: "transform 180ms ease, box-shadow 180ms ease",
      "&:hover": {
        boxShadow: isLightMode
          ? "var(--shadow-md)"
          : `0 8px 32px rgba(0,0,0,0.5), 0 0 20px ${getBorderColor()}15`,
        transform: "translateY(-1px)",
      },
      // Rendu épuré (ex. bornes Plugy) : on neutralise toute la "carte".
      ...(bare ? {
        backgroundColor: "transparent",
        backdropFilter: "none",
        border: "none",
        borderTop: "none",
        boxShadow: "none",
        "&:hover": { boxShadow: "none", transform: "none" },
      } : {}),
    }}>
      {/* Header avec titre et icône - simplifié pour vanne thermo (masqué si bare) */}
      {bare ? null : isVanneThermo ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: 1.5,
            pt: 0.5,
            pb: 0,
          }}
        >
          {getTypeIcon()}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            pt: 1,
            pb: 0.5,
          }}
        >
          {/* Titre et label température */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {getTypeIcon()}
            <Box>
              {title && (
                <Typography
                  sx={{
                    fontFamily: isLightMode ? "var(--font-display)" : undefined,
                    fontSize: isLightMode ? 15 : 14,
                    fontWeight: 600,
                    letterSpacing: isLightMode ? "-0.01em" : undefined,
                    color: isLightMode ? "var(--text-primary)" : "rgba(255,255,255,0.9)",
                  }}
                >
                  {title}
                </Typography>
              )}
              {temperatureLabel && (
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: temperatureLabel.toLowerCase().includes("négative") ? "#f59e0b" : "#22c55e",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {temperatureLabel}
                </Typography>
              )}
            </Box>
          </Box>
          {/* Subtitle à droite si présent */}
          {subtitle && (
            <Typography
              sx={{
                fontSize: 11,
                color: isLightMode ? "#5a6b5a" : "rgba(255,255,255,0.5)",
                fontStyle: "italic",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
        <ResponsiveContainer aspect={aspect} width="100%">
          {chartType === "bar" ? (
            <BarChart
              data={filteredData}
              margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
              onMouseMove={(state) => {
                if (state?.activeTooltipIndex !== undefined) {
                  setHoveredIndex(state.activeTooltipIndex);
                }
              }}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={(state) => {
                // When a bar is clicked, call onBarClick with the data entry
                if (onBarClick && state?.activePayload?.length > 0) {
                  const clickedData = state.activePayload[0].payload;
                  onBarClick(clickedData);
                }
              }}
              style={{ cursor: onBarClick ? 'pointer' : 'default' }}
            >
              <defs>
                {/* Gradient Eau - Bleu vif */}
                <linearGradient id="waterGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#81d4fa" stopOpacity={1}/>
                  <stop offset="50%" stopColor="#4fc3f7" stopOpacity={0.95}/>
                  <stop offset="100%" stopColor="#29b6f6" stopOpacity={0.9}/>
                </linearGradient>
                <linearGradient id="waterGradientHover" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#b3e5fc" stopOpacity={1}/>
                  <stop offset="50%" stopColor="#81d4fa" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#4fc3f7" stopOpacity={0.95}/>
                </linearGradient>
                {/* Gradient Électricité - Orange/Jaune */}
                <linearGradient id="elecGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ffe082" stopOpacity={1}/>
                  <stop offset="50%" stopColor="#ffd54f" stopOpacity={0.95}/>
                  <stop offset="100%" stopColor="#ffca28" stopOpacity={0.9}/>
                </linearGradient>
                <linearGradient id="elecGradientHover" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#fff59d" stopOpacity={1}/>
                  <stop offset="50%" stopColor="#ffe082" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#ffd54f" stopOpacity={0.95}/>
                </linearGradient>
                {/* Gradient par défaut - Vert */}
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#81c784" stopOpacity={1}/>
                  <stop offset="50%" stopColor="#66bb6a" stopOpacity={0.95}/>
                  <stop offset="100%" stopColor="#4caf50" stopOpacity={0.9}/>
                </linearGradient>
                {/* Glow effect */}
                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
                <filter id="dropshadow" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.3"/>
                </filter>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={isLightMode ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.06)"}
                vertical={false}
              />
              <XAxis
                dataKey="time_created"
                type={"category"}
                tickFormatter={formatTime}
                tick={{ fontSize: isMobile ? 9 : 11, fill: isLightMode ? '#3d4a3d' : 'rgba(255,255,255,0.5)' }}
                interval={isMobile ? 3 : 1}
                axisLine={{ stroke: isLightMode ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.08)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: isMobile ? 9 : 11, fill: isLightMode ? '#3d4a3d' : 'rgba(255,255,255,0.5)' }}
                axisLine={false}
                tickLine={false}
                width={55}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: "rgba(255,255,255,0.03)", radius: 4 }}
              />
              {/* Ligne de moyenne */}
              {averageValue > 0 && (
                <ReferenceLine
                  y={averageValue}
                  stroke="rgba(255,255,255,0.3)"
                  strokeDasharray="5 5"
                  strokeWidth={1}
                  label={{
                    value: `moy: ${averageValue}`,
                    position: 'right',
                    fontSize: 10,
                    fill: 'rgba(255,255,255,0.4)',
                  }}
                />
              )}
              <Bar
                dataKey={valueName}
                isAnimationActive={true}
                animationDuration={800}
                animationEasing="ease-out"
                radius={[6, 6, 0, 0]}
                filter={isLightMode ? undefined : "url(#dropshadow)"}
              >
                {filteredData.map((entry, index) => {
                  const isHovered = hoveredIndex === index;
                  const isWater = unit?.toLowerCase() === "litres";
                  const isElec = unit?.toLowerCase() === "kwh";

                  let fillUrl = "url(#barGradient)";
                  if (colorOverride) {
                    fillUrl = colorOverride;
                  } else if (isWater) {
                    fillUrl = isHovered ? "url(#waterGradientHover)" : "url(#waterGradient)";
                  } else if (isElec) {
                    fillUrl = isHovered ? "url(#elecGradientHover)" : "url(#elecGradient)";
                  }

                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={fillUrl}
                      filter={isLightMode ? undefined : (isHovered ? "url(#glow)" : "url(#dropshadow)")}
                      style={{
                        transition: 'all 0.2s ease',
                        transform: isHovered ? 'scaleY(1.02)' : 'scaleY(1)',
                        transformOrigin: 'bottom',
                      }}
                    />
                  );
                })}
              </Bar>
              {/* Seuils d'alerte — après <Bar> pour passer au-dessus */}
              {groupedBy === "daily" && thresholds?.yellow > 0 && (
                <ReferenceLine
                  y={thresholds.yellow}
                  stroke="#fedd37"
                  strokeDasharray="0"
                  strokeWidth={2}
                  label={<ThresholdPillLabel value={thresholds.yellow} bg="#fedd37" fg="#000" />}
                />
              )}
              {groupedBy === "daily" && thresholds?.red > 0 && (
                <ReferenceLine
                  y={thresholds.red}
                  stroke="#fe3e26"
                  strokeDasharray="0"
                  strokeWidth={2}
                  label={<ThresholdPillLabel value={thresholds.red} bg="#fe3e26" fg="#fff" />}
                />
              )}
            </BarChart>
          ) : (
            <AreaChart
              data={filteredData}
              margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
            >
              <defs>
                {/* Gradient bleu clair pour températures positives */}
                <linearGradient id="temperatureGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E0F4FF" stopOpacity={0.95}/>
                  <stop offset="35%" stopColor="#93C5FD" stopOpacity={0.65}/>
                  <stop offset="75%" stopColor="#3B82F6" stopOpacity={0.25}/>
                  <stop offset="100%" stopColor="#1E3A5F" stopOpacity={0.04}/>
                </linearGradient>
                {/* Gradient givré pour températures négatives (chambres froides) */}
                <linearGradient id="glacialGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F0FAFF" stopOpacity={1}/>
                  <stop offset="30%" stopColor="#BAE6FD" stopOpacity={0.8}/>
                  <stop offset="65%" stopColor="#38BDF8" stopOpacity={0.35}/>
                  <stop offset="100%" stopColor="#0C4A6E" stopOpacity={0.04}/>
                </linearGradient>
                {/* Gradient orange pour vanne thermo / chauffage */}
                <linearGradient id="vanneThermGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FFEDD5" stopOpacity={0.9}/>
                  <stop offset="40%" stopColor="#FDBA74" stopOpacity={0.6}/>
                  <stop offset="80%" stopColor="#EA580C" stopOpacity={0.3}/>
                  <stop offset="100%" stopColor="#7C2D12" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={isLightMode ? "rgba(0,0,0,0.1)" : theme.palette.divider} strokeOpacity={0.3} />
              <XAxis
                dataKey="time_created"
                type={"category"}
                tickFormatter={formatTime}
                tick={{ fontSize: isMobile ? 10 : 12, fill: isLightMode ? '#3d4a3d' : undefined }}
                interval={isMobile ? 3 : 1}
                axisLine={{ stroke: isLightMode ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.1)' }}
                tickLine={{ stroke: isLightMode ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.1)' }}
              />
              <YAxis
                tick={{ fontSize: isMobile ? 10 : 12, fill: isLightMode ? '#3d4a3d' : undefined }}
                axisLine={{ stroke: isLightMode ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.1)' }}
                tickLine={{ stroke: isLightMode ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.1)' }}
                width={55}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "transparent" }} />
              <Area
                type="monotone"
                dataKey={valueName}
                fill={
                  isVanneThermo
                    ? "url(#vanneThermGradient)"
                    : temperatureLabel?.toLowerCase().includes("négative") || temperatureLabel?.toLowerCase().includes("negative")
                      ? "url(#glacialGradient)"
                      : "url(#temperatureGradient)"
                }
                stroke={
                  isVanneThermo
                    ? "#F97316"
                    : temperatureLabel?.toLowerCase().includes("négative") || temperatureLabel?.toLowerCase().includes("negative")
                      ? "#BAE6FD"
                      : "#93C5FD"
                }
                strokeWidth={2.5}
                dot={hasTempThresholds ? renderThresholdDot : false}
                activeDot={{ r: 4, fill: isVanneThermo ? "#F97316" : "#BAE6FD", strokeWidth: 0 }}
                isAnimationActive={true}
              />
              {/* Seuils d'alerte température — lignes de référence jaune/rouge.
                  Contrairement aux barres eau/élec, on les affiche aussi en vue
                  Jour (hourly) car c'est la vue principale de la température. */}
              {thresholds?.yellow != null && (
                <ReferenceLine
                  y={thresholds.yellow}
                  stroke="#fedd37"
                  strokeDasharray="0"
                  strokeWidth={2}
                  label={<ThresholdPillLabel value={thresholds.yellow} bg="#fedd37" fg="#000" />}
                />
              )}
              {thresholds?.red != null && (
                <ReferenceLine
                  y={thresholds.red}
                  stroke="#fe3e26"
                  strokeDasharray="0"
                  strokeWidth={2}
                  label={<ThresholdPillLabel value={thresholds.red} bg="#fe3e26" fg="#fff" />}
                />
              )}
            </AreaChart>
          )}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
